import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb, Button, Chip, ContentCard, Icon, MediaCarousel, SectionHeader } from "@zorni/components-lib";

import styles from "./ReservationPreviewPage.module.css";
import { useReservation } from "../../components/trips/myTrips/hooks/useReservation ";
import {
  getTravelerCount,
  useReservationList,
} from "../../components/trips/myTrips/hooks/useReservationList ";
import { useZorni } from "../../hooks/useZorni";
import { currentLocale } from "../../../common/i18n/locale";

const toArray = (value) => (Array.isArray(value) ? value : []);

/* Les prestations arrivent dédupliquées par id, avec leur quantité. */
/* Le détail renvoie les prestations achetées sans titre ni prix : l'API expose
   l'identifiant de la ligne d'achat, pas celui de l'option, et n'envoie pas du
   tout les activités. On écarte donc ce qui n'a pas de libellé plutôt que
   d'afficher des lignes vides. À rouvrir quand l'API les résoudra. */
const groupPurchased = (items) => {
  const grouped = new Map();

  toArray(items).filter((item) => String(item?.title ?? "").trim()).forEach((item, index) => {
    const id = String(item?.id ?? item?.title ?? `item-${index}`);
    const current = grouped.get(id);

    if (current) {
      current.count += 1;
      return;
    }

    grouped.set(id, { ...item, id, count: Number(item?.count ?? 1) });
  });

  return Array.from(grouped.values());
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLong = (value) => {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleDateString(currentLocale(), { day: "2-digit", month: "long", year: "numeric" });
};

const formatShort = (value) => {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleDateString(currentLocale(), { day: "2-digit", month: "short" });
};

/* Plage de dates confiée à Intl : chaque langue a son ordre et sa ponctuation
   — « 19–26 octobre 2026 » en français, « October 19 – 26, 2026 » en anglais.
   Une concaténation maison donnait « 19 → October 26, 2026 ». */
const formatRange = (start, end) => {
  const from = parseDate(start);
  const to = parseDate(end);
  if (!from || !to) return "";

  const options = { day: "numeric", month: "long", year: "numeric" };

  try {
    return new Intl.DateTimeFormat(currentLocale(), options).formatRange(from, to);
  } catch {
    // formatRange manque sur les navigateurs anciens : on retombe sur deux dates.
    return `${formatLong(start)} – ${formatLong(end)}`;
  }
};

const formatPrice = (price) => {
  const value = Number(price?.value ?? 0);
  const currency = String(price?.currency ?? "").trim();

  if (!currency) return String(value);

  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // Devise hors ISO : on affiche le code tel quel plutôt que de planter.
    return `${new Intl.NumberFormat(currentLocale()).format(value)} ${currency}`;
  }
};

const hasAmount = (price) => Number(price?.value ?? 0) > 0;

const ReservationPreviewPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reservationId } = useParams();
  const { userId, isAuthenticated } = useZorni();
  const { reservation, loading, error } = useReservation(reservationId);

  /* QR_GET_TRIP_RESERVATION ne renvoie pas `tripDate` (null côté API) alors que
     la liste le porte : on récupère les dates par là, sinon la page affiche
     « Invalid Date ». À retirer dès que le détail renverra la date. */
  const { list: reservationList } = useReservationList({
    paginationParams: { currentPage: 0, sizePage: 1000 },
    tripPublicationId: null,
    ownerId: userId,
    skip: !isAuthenticated || !userId,
  });

  const fromList = useMemo(
    () => reservationList.find((item) => item?.id === reservationId) ?? null,
    [reservationList, reservationId]
  );

  const trip = reservation?.tripPublicationResume?.trip ?? {};
  const tripDate = reservation?.tripDate ?? fromList?.tripDate ?? {};
  const pricing = reservation?.pricingDetails ?? fromList?.pricingDetails ?? {};

  const clients = toArray(reservation?.clients);
  const activities = groupPurchased(reservation?.activitesPurchased);
  const options = groupPurchased(reservation?.optionsPurchased);

  const status = String(reservation?.status ?? "").trim().toUpperCase();
  const isConfirmed = status === "CONFIRMED";
  const isPassed = status === "PASSED";

  const statusLabel = isConfirmed
    ? t("reservation.status.confirmed", "Confirmée")
    : isPassed
    ? t("reservation.status.passed", "Terminé")
    : t("reservation.status.pending", "En attente");

  const picture =
    toArray(trip?.pictures).find((item) => item?.isMain)?.link ||
    toArray(trip?.pictures)[0]?.link ||
    "";

  const country = toArray(trip?.tripProgramDetails)[0]?.country || "";

  /* Le programme sert de rappel : on le trie par rang, comme sur la page détail. */
  const programSteps = useMemo(
    () =>
      [...toArray(reservation?.tripPublicationResume?.trip?.tripProgramDetails)].sort(
        (a, b) => Number(a?.ranking?.rank ?? 0) - Number(b?.ranking?.rank ?? 0)
      ),
    [reservation]
  );
  const reference = String(reservationId ?? "").slice(0, 8).toUpperCase();

  const nights = Number(tripDate?.nbNight ?? 0) || 0;
  const days = Number(tripDate?.nbDays ?? (nights ? nights + 1 : 0)) || 0;
  const travelers =
    getTravelerCount(reservation?.reservedPlaces ?? fromList?.reservedPlaces) || clients.length;

  const startDate = tripDate?.startDate;
  const endDate = tripDate?.endDate;
  const datesKnown = Boolean(parseDate(startDate) && parseDate(endDate));

  const priceLines = [
    {
      label: t("reservation.price.trip", "Voyage"),
      value: pricing?.travelTotalPriceWithoutCharge ?? pricing?.travelTotalPrice,
      always: true,
    },
    { label: t("reservation.price.activities", "Activités"), value: pricing?.activitiesTotalPrice },
    { label: t("reservation.price.options", "Options"), value: pricing?.optionsTotalPrice },
    { label: t("reservation.price.serviceFees", "Frais de service"), value: pricing?.serviceFees },
    { label: t("reservation.price.paymentFees", "Frais de paiement"), value: pricing?.paimentFees },
  ].filter((line) => line.always || hasAmount(line.value));

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p className={styles.feedback}>{t("reservation.loading", "Chargement de votre dossier…")}</p>
        </div>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p className={styles.feedback}>
            {t("reservation.error", "Impossible de charger cette réservation.")}
          </p>
          <Button
            variant="secondary"
            size="medium"
            iconName="iconArrowLeft"
            iconPosition="left"
            label={t("reservation.back", "Retour à mes voyages")}
            onClick={() => navigate("/my-trips")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <Breadcrumb
          className={styles.breadcrumb}
          ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
          linkComponent={Link}
          items={[
            { label: t("trips.results.home", "Accueil"), to: "/" },
            { label: t("components.myTrips.pageTitle", "Mes voyages"), to: "/my-trips" },
            {
              label: t("myTrips.reference", {
                reference,
                defaultValue: `Réf. ${reference}`,
              }),
            },
          ]}
        />

        <SectionHeader
          size="page"
          className={styles.header}
          kicker={t("reservation.kicker", "Dossier de réservation")}
          title={trip?.title || t("reservation.fallbackTitle", "Votre voyage")}
          subtitle={trip?.smallDescription}
        />

        {/* ---------- Le billet ---------- */}
        <section className={styles.ticket} aria-label={t("reservation.ticket", "Votre billet")}>
          <div className={styles.ticketPhoto}>
            {picture ? <img src={picture} alt="" /> : <span className={styles.noPhoto} />}
            <span className={`${styles.status} ${isConfirmed ? styles.ok : styles.wait}`}>
              {statusLabel}
            </span>
          </div>

          <div className={styles.ticketMain}>
            <dl className={styles.facts}>
              <div>
                <dt>{t("reservation.facts.dates", "Dates réservées")}</dt>
                <dd>
                  {datesKnown
                    ? formatRange(startDate, endDate)
                    : t("reservation.facts.datesUnknown", "À confirmer")}
                </dd>
              </div>
              <div>
                <dt>{t("reservation.facts.duration", "Durée")}</dt>
                <dd>
                  {nights > 0
                    ? t("myTrips.nights", { count: nights, defaultValue: `${nights} nuits` })
                    : "—"}
                  {days > 0 && (
                    <small>
                      {" · "}
                      {t("reservation.facts.days", { count: days, defaultValue: `${days} j` })}
                    </small>
                  )}
                </dd>
              </div>
              <div>
                <dt>{t("reservation.facts.travelers", "Voyageurs")}</dt>
                <dd>{t("myTrips.travelers", { count: travelers, defaultValue: `${travelers} voyageurs` })}</dd>
              </div>
              {country && (
                <div>
                  <dt>{t("reservation.facts.destination", "Destination")}</dt>
                  <dd>{country}</dd>
                </div>
              )}
            </dl>

            <p className={styles.barcode}>
              <Icon name="iconFile" size="sm" />
              {t("myTrips.reference", { reference, defaultValue: `Réf. ${reference}` })}
            </p>
          </div>

          <aside className={styles.stub}>
            <div>
              <span>{t("reservation.stub.departure", "Départ")}</span>
              <strong>{datesKnown ? formatShort(startDate) : "—"}</strong>
            </div>
            <div>
              <span>{t("reservation.stub.return", "Retour")}</span>
              <strong>{datesKnown ? formatShort(endDate) : "—"}</strong>
            </div>
            <div>
              <span>{t("reservation.stub.nights", "Nuits")}</span>
              <strong>{nights || "—"}</strong>
            </div>
            <div>
              <span>{t("reservation.stub.travelers", "Voyageurs")}</span>
              <strong>{travelers}</strong>
            </div>
          </aside>
        </section>

        <div className={styles.columns}>
          <div className={styles.main}>
            {/* ---------- Programme ---------- */}
            {programSteps.length > 0 && (
              <ContentCard
                title={t("reservation.program", "Programme du voyage")}
                titleId="reservation-program"
                className={`${styles.card} ${styles.programCard}`}
              >
                <MediaCarousel
                  type="text"
                  items={programSteps}
                  t={t}
                  ariaLabel={t("reservation.program", "Programme du voyage")}
                />
              </ContentCard>
            )}

            {/* ---------- Prestations choisies ---------- */}
            {(activities.length > 0 || options.length > 0) && (
              <ContentCard
                title={t("reservation.extras", "Prestations réservées")}
                titleId="reservation-extras"
                className={styles.card}
              >
                {[
                  { key: "act", label: t("reservation.price.activities", "Activités"), items: activities },
                  { key: "opt", label: t("reservation.price.options", "Options"), items: options },
                ]
                  .filter((group) => group.items.length > 0)
                  .map((group) => (
                    <div key={group.key} className={styles.extraGroup}>
                      <h4>{group.label}</h4>
                      <ul className={styles.extras}>
                        {group.items.map((item) => (
                          <li key={item.id}>
                            {item?.picture ? (
                              <img src={item.picture} alt="" loading="lazy" />
                            ) : (
                              <span className={styles.extraIcon} aria-hidden="true">
                                <Icon name="iconActivities" size="sm" />
                              </span>
                            )}

                            <div className={styles.extraBody}>
                              <strong>{item?.title || t("reservation.extra", "Prestation")}</strong>
                              {item?.description && <p>{item.description}</p>}
                            </div>

                            <div className={styles.extraSide}>
                              {item.count > 1 && <Chip variant="meta" label={`×${item.count}`} />}
                              {hasAmount(item?.adultPricePerPerson) && (
                                <span>{formatPrice(item.adultPricePerPerson)}</span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </ContentCard>
            )}
          </div>

          {/* ---------- Récapitulatif ---------- */}
          <aside className={styles.side}>
            <ContentCard
              title={t("reservation.recap", "Récapitulatif")}
              titleId="reservation-recap"
              className={styles.card}
            >
              <ul className={styles.priceList}>
                {priceLines.map((line) => (
                  <li key={line.label}>
                    <span>{line.label}</span>
                    <strong>{formatPrice(line.value)}</strong>
                  </li>
                ))}
              </ul>

              <p className={styles.total}>
                <span>{t("reservation.price.total", "Total")}</span>
                <strong>{formatPrice(pricing?.travelTotalPrice)}</strong>
              </p>

              <div className={styles.actions}>
                <Button
                  variant="primary"
                  size="medium"
                  iconName="iconUsers"
                  iconPosition="left"
                  label={t("myTrips.contactAgency", "Contacter l'agence")}
                  onClick={() => navigate("/messenger")}
                />
                {isPassed && (
                  <Button
                    variant="secondary"
                    size="medium"
                    iconName="iconStar"
                    iconPosition="left"
                    label={t("components.myTrips.leaveReview", "Laisser un avis")}
                    onClick={() => navigate(`/${reservationId}/review`)}
                  />
                )}
                <Button
                  variant="ghost"
                  size="medium"
                  iconName="iconArrowLeft"
                  iconPosition="left"
                  label={t("reservation.back", "Retour à mes voyages")}
                  onClick={() => navigate("/my-trips")}
                />
              </div>
            </ContentCard>

              {/* ---------- Voyageurs ---------- */}
              <ContentCard
                title={t("reservation.travelers", "Voyageurs")}
                titleId="reservation-travelers"
                className={styles.card}
              >
                <ul className={styles.travelers}>
                  {clients.map((client, index) => (
                    <li key={`${client?.firstName}-${client?.lastName}-${index}`}>
                      <span className={styles.avatar} aria-hidden="true">
                        {String(client?.firstName ?? "?").charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <strong>
                          {[client?.firstName, client?.lastName].filter(Boolean).join(" ") ||
                            t("reservation.travelerUnknown", "Voyageur")}
                        </strong>
                        {client?.dateOfBirth && <p>{formatLong(client.dateOfBirth)}</p>}
                      </div>
                      {client?.isMainPerson && (
                        <span className={styles.tag}>{t("booking.travelers.you", "C'est vous")}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </ContentCard>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ReservationPreviewPage;
